import { wait } from '@/helpers';
import { _notNil } from '@/littledash';
import { useCallback, useRef } from 'react';

/** Default retry budget per flight for a pass that failed to fetch the state it needs to settle. */
export const SETTLE_RETRIES = 3;
/** Default delay before each of those retries. */
export const SETTLE_RETRY_MS = 1000;

/** Handed to each pass of a {@link useSingleFlight} task. */
export interface SingleFlightPass {
  /**
   * Queues one more pass, which runs as soon as this one settles — as if a trigger had arrived
   * mid-flight. It adds no delay; a pass retrying a failure should use `retry()` instead.
   */
  requeue: () => void;
  /**
   * Spends one retry from the flight's budget: waits the retry delay, then queues one more pass.
   * Resolves false, without waiting or queuing, once the budget is spent. Every pass of a flight shares
   * one budget, including passes queued by mid-flight triggers; the next flight starts with a full one.
   */
  retry: () => Promise<boolean>;
}

interface SingleFlightOptions {
  retries?: number;
  retryDelayMs?: number;
}

/**
 * Runs `task` at most once at a time. A call made while a flight is running returns the in-flight
 * promise and queues one more pass after the current one; calls during a single pass coalesce into
 * that one extra pass, so a caller never settles on work that started before its call. Every pass in
 * a flight runs the `task` the flight started with, even if a re-render has passed a new one.
 *
 * A pass that rejects ends the flight (and rejects the returned promise) without wedging it — the next
 * call starts a fresh flight.
 */
export const useSingleFlight = (
  task: (pass: SingleFlightPass) => Promise<void>,
  { retries = SETTLE_RETRIES, retryDelayMs = SETTLE_RETRY_MS }: SingleFlightOptions = {}
): (() => Promise<void>) => {
  const inFlightRef = useRef<Promise<void> | null>(null);
  const pendingRef = useRef(false);

  return useCallback((): Promise<void> => {
    if (_notNil(inFlightRef.current)) {
      pendingRef.current = true;
      return inFlightRef.current;
    }
    const requeue = (): void => {
      pendingRef.current = true;
    };
    let retriesRemaining = retries;
    const retry = async (): Promise<boolean> => {
      if (retriesRemaining <= 0) {
        return false;
      }
      retriesRemaining -= 1;
      await wait(retryDelayMs);
      requeue();
      return true;
    };
    const flight = (async (): Promise<void> => {
      do {
        pendingRef.current = false;
        await task({ requeue, retry });
      } while (pendingRef.current);
    })();
    inFlightRef.current = flight;
    void flight
      .finally(() => {
        inFlightRef.current = null;
      })
      .catch(() => undefined);
    return flight;
  }, [task, retries, retryDelayMs]);
};
